import addBtnIcon from 'utils/icon/icon_add-circle-blue.svg';
import DefaultInput from 'components/input/DefaultInput';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import { ILang, ICoupon } from 'utils/types/saleTypes';
import { useTranslation } from 'react-i18next';
import { Image, Switch, Tooltip, Upload } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import PrimaryButton from '../../../components/button/PrimaryButton';
import uploadIcon from '../../../utils/icon/icon_upload-image-black.svg';
import cloudIcon from '../../../utils/icon/icon_cloud-upload-image-black.svg';
import plusButtonIcon from '../../../utils/icon/icon_plus-blue.svg';
import { Trash } from 'iconsax-react';
import { storyApi } from '../../../apis/story/storyApis';
import { productApis } from '../../../apis/package/productApis';
import { ISupportLanguage } from '../../../utils/types/storyTypes';
import SelectBox from '../../../components/SelectBox';
import {
  showApiSuccessMessage,
  showStringErrorMessage,
} from '../../../utils/helper/message';
import { INotice, INoticeDetail } from '../../../utils/types/operationTypes';
import { noticeApis } from '../../../apis/operation/noticeApis';
import DefaultTextArea from '../../../components/input/DefaultTextArea';

interface IProps {
  notice: INotice;
  setNotice: React.Dispatch<React.SetStateAction<INotice>>;
  isLink: boolean;
  setIsLink: React.Dispatch<React.SetStateAction<boolean>>;
}

const NewNoticeLocalizingForm: React.FC<IProps> = ({
  notice,
  setNotice,
  isLink,
  setIsLink,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [supportLangList, setSupportLangList] = useState<ISupportLanguage[]>(
    [],
  );

  const fetchLang = async () => {
    const langRes = await storyApi.supportLang();
    setSupportLangList(langRes);
  };

  useEffect(() => {
    fetchLang();
  }, []);

  const handleInputChange = (e) => {
    const newNotice = { ...notice };
    const { id, value, name } = e.target as {
      id: number;
      value: string;
      name: string;
    };
    newNotice.details = newNotice.details?.map((detail) =>
      Number(detail.id) === Number(id)
        ? {
            ...detail,
            [name]: value,
          }
        : detail,
    );
    setNotice(newNotice);
  };

  const handleAddLang = () => {
    const newNoticeDetail: INoticeDetail = {
      id: 0,
      lang: '',
      title: '',
      notice_id: Number(notice.notice_id),
      contents: '',
      url_link: '',
    };

    setNotice({
      ...notice,
      details: [...(notice.details || []), newNoticeDetail],
    });
  };

  const handleRemoveLang = async (lang: string, id: number) => {
    if (window.confirm('Are you sure you want to remove this language?')) {
      await noticeApis.deleteNoticeDetail(
        notice.project_id,
        Number(notice.notice_id),
        id,
      );
      const newNotice = { ...notice };
      newNotice.details = newNotice.details?.filter((v) => v.lang !== lang);
      setNotice(newNotice);
    }
  };

  const props = {
    name: 'file',
    action: '',
    showUploadList: false,
    accept: 'image/png, image/jpeg',
    async onChange(info) {
      if (info.file.status !== 'uploading') {
        // console.log('uploading ===>', info.file, info.fileList);
      }
      if (info.file.status === 'done') {
      } else if (info.file.status === 'error') {
        console.log('failed ===>', info.file);
      }
    },
  };

  const uploadBanner = async (options, noticeDetail: INoticeDetail) => {
    const { file, onSuccess, onError } = options;
    const isPNG = file.type === 'image/png';
    const isJPG = file.type === 'image/jpeg';

    if (!isPNG && !isJPG) {
      showStringErrorMessage('파일 형식이 올바르지 않습니다.');
      return;
    }

    if (file.size > 8000000) {
      showStringErrorMessage('8MB  이상의 파일을 업로드 할 수 없습니다');
      return;
    }

    if (isPNG || isJPG) {
      setLoading(true);
      try {
        const res = await noticeApis.uploadNoticeBanner(
          notice.project_id,
          Number(notice.notice_id),
          Number(noticeDetail.id),
          file,
        );
        const newNotice = { ...notice };
        newNotice.details = newNotice.details?.map((detail) =>
          detail.lang === noticeDetail.lang
            ? {
                ...detail,
                banner_url: res.banner_url,
              }
            : detail,
        );
        setNotice(newNotice);
        showApiSuccessMessage('이미지가 업로드되었습니다.');
        onSuccess('ok');
      } catch (err) {
        showStringErrorMessage('이미지 업로드에 실패했습니다.');
        console.log('Error', err);
        onError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const canAddLangList = supportLangList
    .filter((v) => !notice.details?.map((v) => v.lang).includes(v.code))
    .map((v, i) => {
      return {
        value: v.code,
        label: v.code_name,
      };
    });

  const handleToggle = (checked) => {
    setIsLink(checked);
  };

  return (
    <LangContainer>
      {notice.details.map((noticeDetail, i) => (
        <div className='section_input'>
          {noticeDetail.lang ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Tooltip title={isLink ? '내용 공지' : 'Link 공지'}>
                  <Switch checked={isLink} onChange={handleToggle} />
                </Tooltip>
                <PrimaryButton
                  text={t('buttons.remove')}
                  height='40px'
                  width='fit-content'
                  bgColor='#ffffff'
                  textColor={theme.colors.red_500}
                  fontWeight={theme.fontWeights.medium}
                  fontSize={theme.fontSizes.normal}
                  border={`1px solid ${theme.colors.gray_6}`}
                  rounded='6px'
                  hoverBgColor={theme.colors.gray_6}
                  activeBgColor={theme.colors.gray_6}
                  selectedBgColor={theme.colors.gray_6}
                  loading={loading}
                  onClick={() =>
                    handleRemoveLang(noticeDetail.lang, noticeDetail.id)
                  }
                  LeftPlaceHolder={<Trash size='24' />}
                />
              </div>

              <div style={{ padding: '10px 0' }}></div>

              {isLink ? (
                <DefaultInput
                  title={'링크'}
                  name='url_link'
                  id={`${noticeDetail.id}`}
                  value={noticeDetail.url_link}
                  codeName={noticeDetail.lang}
                  onChange={handleInputChange}
                  maxLength={60}
                  localizing
                  placeholder={'링크를 입력해주세요'}
                  style={{
                    marginLeft: '0.25rem',
                  }}
                />
              ) : (
                <>
                  <DefaultInput
                    title={'제목'}
                    name='title'
                    id={`${noticeDetail.id}`}
                    value={noticeDetail.title}
                    codeName={noticeDetail.lang}
                    onChange={handleInputChange}
                    localizing
                    maxLength={60}
                    placeholder={'제목을 입력해주세요'}
                    top='true'
                    style={{
                      marginLeft: '0.5rem',
                    }}
                  />

                  <DefaultTextArea
                    name='contents'
                    id={`${noticeDetail.id}`}
                    value={noticeDetail.contents}
                    onChange={handleInputChange}
                    placeholder={'내용을 입력해주세요'}
                    maxLength={2000}
                  />
                </>
              )}

              <div className='image_container'>
                <label className='title_label'>{t('ifYouID.form.image')}</label>
                <>
                  <UploadWrapper>
                    <UploadContentWrapper>
                      <div>
                        {loading ? (
                          <div className='flex-wrap icon'>
                            <LoadingOutlined style={{ fontSize: '50px' }} />
                          </div>
                        ) : noticeDetail?.banner_url ? (
                          <Image
                            src={noticeDetail!.banner_url}
                            alt={`icon`}
                            className='icon'
                          />
                        ) : (
                          <></>
                        )}
                      </div>
                    </UploadContentWrapper>

                    {!loading && (
                      <div className='uploadTotalWrap'>
                        <Upload
                          {...props}
                          maxCount={1}
                          customRequest={(options) =>
                            uploadBanner(options, noticeDetail)
                          }
                        >
                          {noticeDetail?.banner_url ? (
                            <div style={{ marginLeft: 24 }}>
                              <PrimaryButton
                                text={t('buttons.replaceImage')}
                                height='40px'
                                width='fit-content'
                                bgColor='#ffffff'
                                textColor={theme.colors.dark_400}
                                fontWeight={theme.fontWeights.medium}
                                fontSize={theme.fontSizes.normal}
                                border={`1px solid ${theme.colors.gray_6}`}
                                rounded='6px'
                                hoverBgColor={theme.colors.gray_6}
                                activeBgColor={theme.colors.gray_6}
                                selectedBgColor={theme.colors.gray_6}
                                loading={loading}
                                LeftPlaceHolder={
                                  <img src={uploadIcon} alt='upload' />
                                }
                              />
                              <p className='turText'>
                                {t('updateStory.form.required')}
                              </p>
                            </div>
                          ) : (
                            <>
                              <div className='emptyImage'>
                                <img src={cloudIcon} alt='upload cloud' />
                                <p className='emptyImageText'>
                                  {t('updateStory.form.placeholder')}
                                </p>
                                <img src={plusButtonIcon} alt='plus icon' />
                              </div>
                              <p className='turText'>
                                {t('updateStory.form.required')}
                              </p>
                            </>
                          )}
                        </Upload>
                      </div>
                    )}
                  </UploadWrapper>
                </>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className='title'>{t('updateStory.form.language')}</p>
                <SelectBox
                  value={noticeDetail.lang}
                  onChange={async (value: string) => {
                    const detail: INoticeDetail = {
                      id: 0,
                      lang: value,
                      title: '',
                      notice_id: Number(notice.notice_id),
                      contents: '',
                      url_link: '',
                    };

                    const res = await noticeApis.createNoticeDetail(
                      notice.project_id,
                      Number(notice.notice_id),
                      detail,
                    );

                    if (res) {
                      const newNotice = { ...notice };
                      newNotice.details = newNotice.details.map((v) =>
                        v.lang === noticeDetail.lang
                          ? { ...v, lang: value, id: res.id }
                          : v,
                      );
                      setNotice(newNotice);
                    }
                  }}
                  options={canAddLangList}
                />
              </div>
            </>
          )}
        </div>
      ))}

      {canAddLangList.length > 0 && (
        <AddButton onClick={handleAddLang}>
          <img src={addBtnIcon} alt='add' />
          <p>Create new Notice for each language</p>
        </AddButton>
      )}
    </LangContainer>
  );
};

export default NewNoticeLocalizingForm;

const LangContainer = styled.div`
  .section_input {
    margin-bottom: 4rem;
    border-bottom: 1px solid rgba(217, 222, 227, 0.5);
    padding-bottom: 2.5rem;
  }

  .image_container {
    width: 100%;

    .title_label {
      color: ${theme.colors.blue_7};
      font-size: ${theme.fontSizes.md};
      margin-left: 0.5rem;
      font-weight: 700;
    }
  }
`;

const UploadContentWrapper = styled.div`
  .upload {
    border: 1px solid ${theme.colors.gray_7};
    border-style: dotted;
  }

  .flex-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .icon {
    width: 256px;
    object-fit: cover;
  }
`;

const UploadWrapper = styled.div`
  display: flex;
  padding-top: 20px;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;

  .emptyImage {
    border: 1px solid ${theme.colors.dark_100};
    border-radius: 4px;
    border-style: dotted;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px;

    & > * + * {
      margin-top: 10px;
    }

    cursor: pointer;
  }

  .turText {
    font-size: ${theme.fontSizes.sm};
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
    margin-top: 8px;
  }

  .emptyImageText {
    color: ${theme.colors.dark_500};
    font-size: ${theme.fontSizes.normal};
    font-weight: ${theme.fontWeights.normal};
    line-height: 24px;
    letter-spacing: 0.2px;
  }

  .uploadTotalWrap {
    display: flex;

    & > * + * {
      margin-left: 8px;
    }
  }

  & > * + * {
  }
`;

const AddButton = styled.div`
  margin-top: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: 6px;
  }

  p {
    color: ${theme.colors.blue_500};
    font-size: ${theme.fontSizes.normal};
    font-style: normal;
    font-weight: ${theme.fontWeights.normal};
    line-height: 24px;
    letter-spacing: 0.2px;
  }
`;
